import { useEffect, useId, useRef, useState } from 'react';

interface Props {
  id: string;
  value: string;
  onChange: (value: string) => void;
  /** Suggestions, already in the order they should be offered. */
  options: string[];
  placeholder?: string;
  /** Shown above the option list, or a plain "no matches" note below it. */
  emptyNote?: string;
}

const MAX_SUGGESTIONS = 8;

/**
 * A text input with filtered suggestions — never a `<select>` replacement.
 *
 * Built to replace `<input list>` + `<datalist>`, whose dropdown is native
 * browser chrome: unstyleable, and in some layouts renders detached from the
 * input on desktop while working fine on mobile, where it becomes a
 * full-screen picker instead. This owns its own menu as an ordinary
 * absolutely-positioned element in the page's own layout, so there is no
 * browser popup left to mis-position, and it can be styled to match every
 * other control in the form.
 *
 * Always free text. `onChange` fires on every keystroke regardless of
 * whether anything matches — picking a suggestion is a shortcut, never a
 * requirement.
 *
 * Always opens downward, deliberately not viewport-flip-aware. An earlier
 * version flipped the menu above the input when the viewport had less room
 * below than the menu's height — borrowed from FidiPerks' CustomDropdown,
 * which needs that for dropdowns in a fixed header. This menu is a plain
 * `position: absolute` element inside an ordinary scrollable page, so
 * "little room below in the current viewport" just means more of the page
 * is below the fold, not that the menu has nowhere to go — the page can
 * always scroll to reveal it. Flipping on that signal did the opposite of
 * what it was meant to: on a normal multi-field form it opened the menu on
 * top of the field above instead, which is the exact misplacement this
 * component exists to fix. If a future fixed-position use of this component
 * needs a real flip, that is a real requirement to design for then, not a
 * speculative one to carry now.
 *
 * Touch devices (`pointer: coarse`) get a button instead of the input until
 * the person asks to type. Focusing a text input raises the on-screen
 * keyboard, which shoves the page up and covers the very menu that just
 * opened — so on touch, tapping shows the list with no keyboard, and a
 * "Type your own…" row switches to the input (and its keyboard) on purpose.
 * Desktop keeps the input up front: type-ahead there costs nothing.
 */
export function Combobox({ id, value, onChange, options, placeholder, emptyNote }: Props) {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  // Touch only: false shows the button + full list, true swaps in the text input.
  const [typing, setTyping] = useState(false);
  const [touch] = useState(
    () => typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches,
  );
  const pickMode = touch && !typing;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  const query = value.trim().toLowerCase();
  const filtered = pickMode
    ? options
    : (query ? options.filter((o) => o.toLowerCase().includes(query)) : options).slice(
        0,
        MAX_SUGGESTIONS,
      );

  // Closes on an outside click. Deferred to the next tick so the click that
  // opened the menu (e.g. re-focusing the input) is not also the click that
  // immediately closes it.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setTyping(false);
      }
    };

    const id = window.setTimeout(() => document.addEventListener('mousedown', onPointerDown), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  const select = (option: string) => {
    onChange(option);
    setOpen(false);
    setHighlighted(-1);
    setTyping(false);
  };

  const startTyping = () => {
    setTyping(true);
    setHighlighted(-1);
    setOpen(true);
  };

  // Pick-mode keyboard support (hardware keyboards on tablets). Row 0 is the
  // "Type your own…" row, rows 1+ are `options`.
  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const last = options.length; // index of the last row
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlighted((h) => Math.min(h + 1, last));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!open) setOpen(true);
      else if (highlighted === 0) startTyping();
      else if (highlighted > 0) select(options[highlighted - 1]!);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && highlighted >= 0 && filtered[highlighted]) {
        e.preventDefault();
        select(filtered[highlighted]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  if (pickMode) {
    return (
      <div className="combobox" ref={wrapperRef}>
        <button
          id={id}
          type="button"
          className="dropdown-trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={
            open && highlighted >= 0 ? `${listboxId}-${highlighted}` : undefined
          }
          onClick={() => setOpen((o) => !o)}
          onKeyDown={onTriggerKeyDown}
        >
          <span className="dropdown-body">
            {value ? (
              <span className="dropdown-label">{value}</span>
            ) : (
              <span className="dropdown-placeholder">{placeholder ?? 'Choose…'}</span>
            )}
          </span>
          <span className="dropdown-caret" aria-hidden="true">
            ▾
          </span>
        </button>

        {open && (
          <ul id={listboxId} role="listbox" className="combobox-menu">
            <li
              id={`${listboxId}-0`}
              role="option"
              aria-selected={false}
              className={`combobox-option combobox-option--type${highlighted === 0 ? ' combobox-option--highlighted' : ''}`}
              onClick={startTyping}
            >
              Type your own…
            </li>
            {filtered.map((option, i) => (
              <li
                key={option}
                id={`${listboxId}-${i + 1}`}
                role="option"
                aria-selected={option === value}
                className={`combobox-option${i + 1 === highlighted ? ' combobox-option--highlighted' : ''}`}
                onClick={() => select(option)}
              >
                {option}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="combobox" ref={wrapperRef}>
      <input
        id={id}
        type="text"
        // Only reached on touch after "Type your own…" — the keyboard is wanted then.
        autoFocus={typing}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          open && highlighted >= 0 ? `${listboxId}-${highlighted}` : undefined
        }
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlighted(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />

      {open && (
        <ul id={listboxId} role="listbox" className="combobox-menu">
          {filtered.length > 0 ? (
            filtered.map((option, i) => (
              <li
                key={option}
                id={`${listboxId}-${i}`}
                role="option"
                aria-selected={i === highlighted}
                className={`combobox-option${i === highlighted ? ' combobox-option--highlighted' : ''}`}
                // Fires before the input's blur, so selecting a suggestion
                // by click doesn't first close the menu out from under it.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => select(option)}
              >
                {option}
              </li>
            ))
          ) : (
            <li className="combobox-empty">{emptyNote ?? 'No matches — your own text is fine.'}</li>
          )}
        </ul>
      )}
    </div>
  );
}
